import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PromptFormatName, RoundTypes } from '@shared/constants';
import { createRoom } from '../api/endpoints';
import { loadSelectedRoundTypes } from './room/shared/round-type-picker';
import { Header } from './header';
import { Sparkles } from './sparkles';
import { SplitWipe, SPLIT_WIPE_MS, SPLIT_WIPE_STATE } from './split-wipe';
import logoUrl from '../../logo.png';
import './landing.css';

const prefersReducedMotion = () => (
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
);

export const Landing = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  // Set once "Start a new game" is clicked: plays the exit animation and
  // closes the split over the card while the room is created.
  const [leaving, setLeaving] = useState(false);
  const joinRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    document.body.classList.add('landingPage');
    return () => document.body.classList.remove('landingPage');
  }, []);

  const onCreate = async () => {
    if (leaving) {
      return;
    }
    try {
      setError(null);
      setLeaving(true);
      const wipeDone = new Promise(resolve => {
        window.setTimeout(resolve, prefersReducedMotion() ? 0 : SPLIT_WIPE_MS);
      });
      const [data] = await Promise.all([
        createRoom(loadSelectedRoundTypes()),
        wipeDone
      ]);
      navigate(`room/${data.code}`, { state: SPLIT_WIPE_STATE });
    } catch (e: any) {
      setLeaving(false);
      setError(e);
    }
  };

  const onJoin = () => {
    const roomCode = joinRef.current?.value;
    if (!roomCode) {
      setError('Please enter a room code to join a room');
      return;
    }
    navigate(`room/${roomCode}`);
  };

  const onJoinKeyDown = (evt: React.KeyboardEvent<HTMLInputElement>) => {
    if (evt.key === 'Enter') {
      onJoin();
    }
  };

  return (
    <>
      <Header />
      <div className={`landing${leaving ? ' leaving' : ''}`}>
        <Sparkles className="landingDecor" />
        {leaving && <SplitWipe direction="closing" />}
        <div className="landingHero">
          <div className="landingLogoWrap">
            <img className="logo" src={logoUrl} alt="" />
          </div>
          <h1 aria-label="Icebreaker">
            <span className="ice">Ice</span><span className="breaker">breaker</span>
          </h1>
          <p className="landingTagline">
            A party game for teams. One person hosts on the big screen, everyone
            else plays along on their phone.
          </p>
          <ul className="landingRounds">
            {RoundTypes.map(format => (
              <li key={format} data-format={format}>
                {PromptFormatName[format]}
              </li>
            ))}
          </ul>
        </div>
        <div className="landingActions">
          {error && <p className="red">{'' + error}</p>}
          <button className="block shine" onClick={onCreate} aria-busy={leaving}>
            {leaving ? 'Breaking the ice\u2026' : 'Start a new game'}
          </button>
          <div className="landingDivider">or join a game</div>
          <div className="joinRow">
            <input
              ref={joinRef}
              type="text"
              placeholder="Room code"
              autoCapitalize="characters"
              onKeyDown={onJoinKeyDown}
            />
            <button className="reverse" onClick={onJoin}>Join</button>
          </div>
        </div>
      </div>
    </>
  );
};
